import { useLocale as useLocaleR42 } from '@/lib/locale';
import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { HandCoins, Clock } from 'lucide-react';

/**
 * PayAnotherWayOption (Phase 99)
 * ------------------------------
 * Shared "Or pay another way (Venmo / Zelle / cash)" chooser rendered
 * BELOW the Stripe card form on the registration Fee step and the
 * package Payment step. Never rendered for classes (callers gate that).
 *
 * Flow: tap the outline button → the coach's payment instructions expand
 * with a clear "your spot stays pending until the coach confirms" note →
 * parent taps Confirm → `onConfirm()` fires and the caller records the
 * offline choice + advances the wizard.
 *
 * Props:
 *   instructions      — coach's free-text payment instructions
 *   coachDisplayName  — for the pending-confirmation copy
 *   onConfirm()       — parent confirmed the offline choice
 *   confirmLabel      — optional CTA copy override
 *   idPrefix          — data-testid prefix (e.g. "feestep" / "paystep")
 */
export function PayAnotherWayOption({ instructions, coachDisplayName, onConfirm, confirmLabel, idPrefix = 'paw' }) {
    const { t: t42 } = useLocaleR42();
    const [open, setOpen] = useState(false);
    const coach = coachDisplayName || 'your coach';

    return (
        <div className="space-y-3" data-testid={`${idPrefix}-pay-another-way`}>
            <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-[#1a3a5c]/15" />
                <span className="text-xs font-medium uppercase tracking-wide text-[#1a3a5c]/50">{t42("or")}</span>
                <div className="h-px flex-1 bg-[#1a3a5c]/15" />
            </div>
            {!open ? (
                <Button
                    type="button"
                    variant="outline"
                    className="h-12 w-full rounded-xl border-2 border-[#1a3a5c]/20 text-[#0a1628] hover:bg-[#f4f7fb]"
                    onClick={() => setOpen(true)}
                    data-testid={`${idPrefix}-pay-another-way-button`}
                >
                    <HandCoins className="mr-2 h-4 w-4" /> {t42("Pay another way (Venmo, Zelle, cash)")} </Button>
            ) : (
                <Card className="sd-card border-[#ffd700]/50 bg-[#fffbeb] p-4" data-testid={`${idPrefix}-pay-another-way-panel`}>
                    <div className="flex items-start gap-3">
                        <div className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#ffd700]/25">
                            <HandCoins className="h-5 w-5 text-[#0a1628]" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="font-semibold text-[#0a1628]">{t42("How to pay")} {coach}</div>
                            {instructions ? (
                                <p className="mt-1 whitespace-pre-wrap text-sm text-[#1a3a5c]" data-testid={`${idPrefix}-pay-another-way-instructions`}>
                                    {instructions}
                                </p>
                            ) : (
                                <p className="mt-1 text-sm text-[#1a3a5c]" data-testid={`${idPrefix}-pay-another-way-instructions`}>
                                    {coach} {t42("will share payment details with you directly.")} </p>
                            )}
                            <div className="mt-3 flex items-start gap-2 rounded-lg bg-white/70 p-2.5 text-xs text-[#1a3a5c]">
                                <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#0a1628]/70" />
                                <span>
                                    <strong>{t42("Your spot stays pending")}</strong> {t42("until")} {coach} {t42("confirms your payment was received — you'll get a confirmation as soon as they do.")} </span>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                                <Button
                                    type="button"
                                    className="sd-cta sd-focus h-11 rounded-xl"
                                    onClick={onConfirm}
                                    data-testid={`${idPrefix}-pay-another-way-confirm`}
                                >
                                    {confirmLabel || "Continue — I'll pay the coach directly"}
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    className="h-11 rounded-xl text-[#1a3a5c]"
                                    onClick={() => setOpen(false)}
                                    data-testid={`${idPrefix}-pay-another-way-cancel`}
                                > {t42("Never mind, I'll pay by card")} </Button>
                            </div>
                        </div>
                    </div>
                </Card>
            )}
        </div>
    );
}

export default PayAnotherWayOption;
